import React, { useEffect, useState } from "react";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  GlobalStyles,
} from "@mui/material";

import Grid from "@mui/material/Grid";
import { styled, ThemeProvider } from "@mui/material/styles";
import { chartsTooltipClasses } from "@mui/x-charts";

import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

import axios from "axios";
import ConstList from "../utils/ConstList";

import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import FlagCircleRoundedIcon from "@mui/icons-material/FlagCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import theme from "../theme";

import { BarChart } from "@mui/x-charts/BarChart";

/* =========================================================
 型定義
========================================================= */

type SummaryData = {
  total_customer_companies: number;
  current_month_appointment_companies: number;
  active_negotiation_companies: number;
  current_month_won_companies: number;
  follow_required_companies: number;
};

type PipelineCountRow = {
  sales_status_id: number;
  sales_status: string;
  count: number;
};

type MonthlyTrendRow = {
  target_month: string;
  appointment_companies: number;
  won_companies: number;
};

type ServiceInterestYearlyRow = {
  target_year: string;
  proposed_service_id: number;
  proposed_service: string;
  company_count: number;
};

type HotCaseRow = {
  id: number;
  company_id: number;
  company_name: string;
  sales_record_id: number | null;
  sales_date: string | null;
  days_since_activity: number | null;
  sales_status_id: number | null;
  sales_status: string;
  sales_person: string;
  proposed_service: string;
  next_action: string;
  latest_note: string;
  follow_required: boolean;
};

type MemberProgressRow = {
  id: number;
  sales_person_id: number;
  sales_person: string;
  active_companies: number;
  follow_required_companies: number;
  current_month_appointment_companies: number;
  current_month_won_companies: number;
  latest_note: string;
};

type MonthlyGoalData = {
  goals_id: number | null;
  target_month: string;
  monthly_sales_goals: string;
  monthly_kpi: string;
  target: string;
};

type GoalKey = "monthly_sales_goals" | "monthly_kpi" | "target";

type SnackbarSeverity = "success" | "error" | "warning" | "info";

type SnackbarState = {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
};

/* =========================================================
 初期値
========================================================= */

const initialSummaryData: SummaryData = {
  total_customer_companies: 0,
  current_month_appointment_companies: 0,
  active_negotiation_companies: 0,
  current_month_won_companies: 0,
  follow_required_companies: 0,
};

const initialMonthlyGoalData: MonthlyGoalData = {
  goals_id: null,
  target_month: "",
  monthly_sales_goals: "",
  monthly_kpi: "",
  target: "",
};

/* =========================================================
 色定義
 theme.ts の色味に合わせたダッシュボード用カラー
========================================================= */

const dashboardColors = {
  pageBg: "#2D2C3A",
  panel: "#3A414C",
  panelDark: "#1F2530",
  panelDeep: "#1F1D30",
  accent: "#C5E763",
  accentSub: "#98bc2e",
  text: "#FFFFFF",
  textSoft: "rgba(232,238,245,0.78)",
  textMuted: "#9FB0C0",
  border: "rgba(217, 217, 217, 0.12)",
  borderStrong: "rgba(197,231,99,0.28)",
  warning: "#FFC107",
  error: "#ff8579",
};

/* =========================================================
 共通UI
========================================================= */

const DashboardCard = styled(Card)(() => ({
  height: "100%",
  borderRadius: 14,
  backgroundColor: dashboardColors.panel,
  color: dashboardColors.text,
  boxShadow: "0 10px 24px rgba(0,0,0,0.20)",
  border: `1px solid ${dashboardColors.border}`,
  overflow: "hidden",

  "& .MuiCardHeader-title": {
    color: dashboardColors.text,
    fontWeight: 800,
    fontSize: "1rem",
  },

  "& .MuiCardHeader-subheader": {
    color: dashboardColors.textSoft,
    lineHeight: 1.6,
  },
}));

const SectionTitle = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => (
  <Box sx={{ mb: 1.2 }}>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: "0.98rem",
        color: dashboardColors.text,
        letterSpacing: "0.02em",
      }}
    >
      {title}
    </Typography>

    {description && (
      <Typography
        variant="body2"
        sx={{
          mt: 0.5,
          color: dashboardColors.textSoft,
          lineHeight: 1.55,
        }}
      >
        {description}
      </Typography>
    )}
  </Box>
);


const goalMeta: Record<
  GoalKey,
  {
    title: string;
    icon: React.ReactNode;
  }
> = {
  monthly_sales_goals: {
    title: "営業目標",
    icon: <AutoAwesomeRoundedIcon fontSize="small" />,
  },
  monthly_kpi: {
    title: "KPI",
    icon: <TrackChangesRoundedIcon fontSize="small" />,
  },
  target: {
    title: "ターゲット基準",
    icon: <FlagCircleRoundedIcon fontSize="small" />,
  },
};

/* =========================================================
 メインコンポーネント
========================================================= */

const Prospecting_strategy = () => {
  const loginUserData = useRecoilValue(LoginUserAtom);

  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  };

  const formatMonthLabel = (month: string) => {
    if (!month || !/^\d{4}-\d{2}$/.test(month)) return "今月";
    const [year, mon] = month.split("-");
    return `${year}年${mon}月`;
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";

    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");

    return `${year}/${month}/${day}`;
  };

  const formatMonthShort = (month: string) => {
    if (!month || !/^\d{4}-\d{2}$/.test(month)) return month;
    const [, mon] = month.split("-");
    return `${Number(mon)}月`;
  };

  const [loading, setLoading] = useState<boolean>(false);
  const [goalSaving, setGoalSaving] = useState<boolean>(false);

  const [summaryData, setSummaryData] =
    useState<SummaryData>(initialSummaryData);

  const [pipelineData, setPipelineData] = useState<PipelineCountRow[]>([]);
  const [monthlyTrendData, setMonthlyTrendData] = useState<MonthlyTrendRow[]>(
    []
  );

  const [serviceInterestYearlyData, setServiceInterestYearlyData] = useState<
    ServiceInterestYearlyRow[]
  >([]);

  const [hotCases, setHotCases] = useState<HotCaseRow[]>([]);
  const [memberProgress, setMemberProgress] = useState<MemberProgressRow[]>([]);


  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [monthlyGoalData, setMonthlyGoalData] = useState<MonthlyGoalData>(
    initialMonthlyGoalData
  );

  const [editingKey, setEditingKey] = useState<GoalKey | null>(null);
  const [draftValue, setDraftValue] = useState<string>("");
  const [selectedMonth] = useState<string>(getCurrentMonth());

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: SnackbarSeverity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  /* =========================================================
   共通スタイル
  ========================================================= */

const tooltipSx = {
  [`& .${chartsTooltipClasses.paper}`]: {
    backgroundColor: `${dashboardColors.panelDark} !important`,
    color: `${dashboardColors.text} !important`,
    border: `1px solid ${dashboardColors.border}`,
    borderRadius: "8px",
    boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
  },

  [`& .${chartsTooltipClasses.table}`]: {
    color: `${dashboardColors.text} !important`,
  },

  [`& .${chartsTooltipClasses.row}`]: {
    color: `${dashboardColors.text} !important`,
  },

  [`& .${chartsTooltipClasses.cell}`]: {
    color: `${dashboardColors.text} !important`,
  },

  [`& .${chartsTooltipClasses.labelCell}`]: {
    color: `${dashboardColors.textSoft} !important`,
  },

  [`& .${chartsTooltipClasses.valueCell}`]: {
    color: `${dashboardColors.text} !important`,
    fontWeight: 800,
  },

  [`& .${chartsTooltipClasses.markCell}`]: {
    color: `${dashboardColors.text} !important`,
  },
};

const chartSx = {
  "& .MuiChartsAxis-line": {
    stroke: "rgba(232,238,245,0.35)",
  },
  "& .MuiChartsAxis-tick": {
    stroke: "rgba(232,238,245,0.35)",
  },
  "& .MuiChartsAxis-tickLabel": {
    fill: dashboardColors.textSoft,
    fontSize: 12,
  },
  "& .MuiChartsLegend-label": {
    fill: dashboardColors.textSoft,
  },
  "& .MuiChartsAxis-label": {
    fill: dashboardColors.textSoft,
  },

  ...tooltipSx,
};

  const gridSx = {
    border: `1px solid ${dashboardColors.border}`,
    borderRadius: 2,
    color: dashboardColors.text,
    backgroundColor: dashboardColors.panelDark,
    overflow: "hidden",

    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: dashboardColors.panelDeep,
      color: dashboardColors.text,
      borderBottom: `1px solid ${dashboardColors.border}`,
    },

    "& .MuiDataGrid-columnHeader": {
      backgroundColor: dashboardColors.panelDeep,
    },

    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 700,
      color: dashboardColors.text,
    },

    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${dashboardColors.border}`,
      color: dashboardColors.textSoft,
    },

    "& .MuiDataGrid-row:hover": {
      backgroundColor: "rgba(197,231,99,0.06)",
    },

    "& .MuiDataGrid-footerContainer": {
      borderTop: `1px solid ${dashboardColors.border}`,
      backgroundColor: dashboardColors.panelDeep,
      color: dashboardColors.textSoft,
    },

    "& .MuiTablePagination-root": {
      color: dashboardColors.textSoft,
    },

    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
      color: dashboardColors.textSoft,
    },

    "& .MuiSvgIcon-root": {
      color: dashboardColors.textSoft,
    },

    "& .MuiDataGrid-overlay": {
      backgroundColor: dashboardColors.panelDark,
      color: dashboardColors.textSoft,
    },
  };

  /* =========================================================
   データ取得
  ========================================================= */

  const fetchData = async () => {
    setLoading(true);
    setErrorMessage(null);

    const postData = {
      flag: "prospecting_strategy",
      access_token: loginUserData.accessToken,
      algorithm: loginUserData.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "prospectingStrategy.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      const resData = response?.data?.data;

      if (resData === false) {
        setErrorMessage(response?.data?.message ?? "データ取得に失敗しました。");
        return;
      }

      if (!resData || typeof resData !== "object") {
        setErrorMessage("取得データの形式が不正です。");
        return;
      }

      const summary: SummaryData = {
        total_customer_companies: Number(
          resData.summary?.total_customer_companies ?? 0
        ),
        current_month_appointment_companies: Number(
          resData.summary?.current_month_appointment_companies ?? 0
        ),
        active_negotiation_companies: Number(
          resData.summary?.active_negotiation_companies ?? 0
        ),
        current_month_won_companies: Number(
          resData.summary?.current_month_won_companies ?? 0
        ),
        follow_required_companies: Number(
          resData.summary?.follow_required_companies ?? 0
        ),
      };

      const pipeline: PipelineCountRow[] = (
        resData.pipeline_counts ?? []
      ).map((item: any) => ({
        sales_status_id: Number(item.sales_status_id ?? 0),
        sales_status: item.sales_status ?? "",
        count: Number(item.count ?? 0),
      }));

      const monthlyTrend: MonthlyTrendRow[] = (
        resData.monthly_trend ?? []
      ).map((item: any) => ({
        target_month: item.target_month ?? "",
        appointment_companies: Number(item.appointment_companies ?? 0),
        won_companies: Number(item.won_companies ?? 0),
      }));

      const serviceInterestYearly: ServiceInterestYearlyRow[] = (
        resData.service_interest_yearly ?? []
      ).map((item: any) => ({
        target_year: item.target_year ?? "",
        proposed_service_id: Number(item.proposed_service_id ?? 0),
        proposed_service: item.proposed_service ?? "",
        company_count: Number(item.company_count ?? 0),
      }));

      const hotCaseRows: HotCaseRow[] = (resData.hot_cases ?? []).map(
        (item: any, index: number) => ({
          id: Number(item.company_id ?? index + 1),
          company_id: Number(item.company_id ?? 0),
          company_name: item.company_name ?? "",
          sales_record_id:
            item.sales_record_id !== null && item.sales_record_id !== undefined
              ? Number(item.sales_record_id)
              : null,
          sales_date: item.sales_date ?? null,
          days_since_activity:
            item.days_since_activity !== null &&
            item.days_since_activity !== undefined
              ? Number(item.days_since_activity)
              : null,
          sales_status_id:
            item.sales_status_id !== null && item.sales_status_id !== undefined
              ? Number(item.sales_status_id)
              : null,
          sales_status: item.sales_status ?? "",
          sales_person: item.sales_person ?? "",
          proposed_service: item.proposed_service ?? "",
          next_action: item.next_action ?? "",
          latest_note: item.latest_note ?? "",
          follow_required: Boolean(item.follow_required),
        })
      );

      const memberRows: MemberProgressRow[] = (
        resData.member_progress ?? []
      ).map((item: any, index: number) => ({
        id: Number(item.sales_person_id ?? index + 1),
        sales_person_id: Number(item.sales_person_id ?? 0),
        sales_person: item.sales_person ?? "",
        active_companies: Number(item.active_companies ?? 0),
        follow_required_companies: Number(
          item.follow_required_companies ?? 0
        ),
        current_month_appointment_companies: Number(
          item.current_month_appointment_companies ?? 0
        ),
        current_month_won_companies: Number(
          item.current_month_won_companies ?? 0
        ),
        latest_note: item.latest_note ?? "",
      }));

      setSummaryData(summary);
      setPipelineData(pipeline);
      setMonthlyTrendData(monthlyTrend);
      setServiceInterestYearlyData(serviceInterestYearly);
      setHotCases(hotCaseRows);
      setMemberProgress(memberRows);
    } catch (error) {
      console.error("データ取得エラー:", error);
      setErrorMessage("データの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
   月次営業目標取得
  ========================================================= */

  const fetchMonthlyGoals = async (month: string) => {
    try {
      const postData = {
        flag: "get_monthly_sales_goals",
        target_month: month,
        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
      };

      const response = await axios.post(
        ConstList.getApiUrl() + "prospectingStrategy.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      if (!response?.data) {
        throw new Error("営業目標の取得に失敗しました。");
      }

      if (response.data.data === false) {
        throw new Error(
          response?.data?.message ?? "営業目標の取得に失敗しました。"
        );
      }

      const row = response.data.data;

      if (!row) {
        setMonthlyGoalData({
          goals_id: null,
          target_month: `${month}-01`,
          monthly_sales_goals: "",
          monthly_kpi: "",
          target: "",
        });
        return;
      }

      setMonthlyGoalData({
        goals_id: row.goals_id ?? null,
        target_month: row.target_month ? String(row.target_month) : `${month}-01`,
        monthly_sales_goals: row.monthly_sales_goals ?? "",
        monthly_kpi: row.monthly_kpi ?? "",
        target: row.target ?? "",
      });
    } catch (error: any) {
      console.error("月次目標取得エラー:", error);
      setErrorMessage(error?.message ?? "営業目標の取得に失敗しました。");
    }
  };

  const handleGoalEditStart = (key: GoalKey) => {
    setEditingKey(key);
    setDraftValue(monthlyGoalData[key] ?? "");
  };

  const handleGoalEditCancel = () => {
    setEditingKey(null);
    setDraftValue("");
  };

  const handleGoalEditSave = async () => {
    if (!editingKey) return;

    const nextData: MonthlyGoalData = {
      ...monthlyGoalData,
      [editingKey]: draftValue,
    };

    try {
      setGoalSaving(true);

      const postData = {
        flag: "save_monthly_sales_goals",
        target_month: selectedMonth,
        monthly_sales_goals: nextData.monthly_sales_goals,
        monthly_kpi: nextData.monthly_kpi,
        target: nextData.target,
        access_token: loginUserData.accessToken,
        algorithm: loginUserData.algorithm,
      };

      const response = await axios.post(
        ConstList.getApiUrl() + "prospectingStrategy.php",
        postData,
        ConstList.AXIOS_CONFIG
      );

      if (!response?.data || response.data.data === false || !response.data.data) {
        throw new Error(response?.data?.message ?? "保存に失敗しました。");
      }

      const row = response.data.data;

      setMonthlyGoalData({
        goals_id: row.goals_id ?? nextData.goals_id,
        target_month: row.target_month
          ? String(row.target_month)
          : nextData.target_month,
        monthly_sales_goals:
          row.monthly_sales_goals ?? nextData.monthly_sales_goals,
        monthly_kpi: row.monthly_kpi ?? nextData.monthly_kpi,
        target: row.target ?? nextData.target,
      });

      setEditingKey(null);
      setDraftValue("");
      showSnackbar("保存しました", "success");
    } catch (error: any) {
      console.error("月次営業目標保存エラー:", error);
      showSnackbar(
        error?.message ?? "月次営業目標の保存に失敗しました。",
        "error"
      );
    } finally {
      setGoalSaving(false);
    }
  };

  /* =========================================================
   初期処理
  ========================================================= */

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchMonthlyGoals(selectedMonth);
  }, [selectedMonth]);

  /* =========================================================
   表示用データ
  ========================================================= */

  const pipelineChartData = pipelineData.map((item) => ({
    label: item.sales_status,
    value: item.count,
  }));

  const monthlyTrendChartData = monthlyTrendData.map((item) => ({
    month: formatMonthShort(item.target_month),
    appointment_companies: item.appointment_companies,
    won_companies: item.won_companies,
  }));

  const serviceMasterList = Array.from(
    new Map(
      serviceInterestYearlyData.map((item) => [
        item.proposed_service_id,
        {
          id: item.proposed_service_id,
          name: item.proposed_service,
        },
      ])
    ).values()
  ).sort((a, b) => a.id - b.id);

  const serviceInterestYearlyChartData = Array.from(
    new Set(serviceInterestYearlyData.map((item) => item.target_year))
  )
    .sort()
    .map((year) => {
      const row: Record<string, string | number> = {
        year,
      };

      serviceMasterList.forEach((service) => {
        const found = serviceInterestYearlyData.find(
          (item) =>
            item.target_year === year &&
            item.proposed_service_id === service.id
        );

        row[`service_${service.id}`] = found?.company_count ?? 0;
      });

      return row;
    });

  const serviceInterestYearlySeries = serviceMasterList.map((service) => ({
    dataKey: `service_${service.id}`,
    label: service.name,
    stack: "service",
  }));

  const summaryCards = [
    {
      label: "今月アポ獲得社数",
      value: summaryData.current_month_appointment_companies,
      unit: "社",
      icon: <GroupsOutlinedIcon />,
    },
    {
      label: "商談進行中",
      value: summaryData.active_negotiation_companies,
      unit: "社",
      icon: <TrendingUpOutlinedIcon />,
    },
    {
      label: "今月成約",
      value: summaryData.current_month_won_companies,
      unit: "社",
      icon: <HandshakeOutlinedIcon />,
    },
    {
      label: "要フォロー",
      value: summaryData.follow_required_companies,
      unit: "件",
      description: "30日以上動きがない進行中案件",
      icon: <WarningAmberOutlinedIcon />,
    },
  ];


  const getFollowRequiredReason = (row: HotCaseRow) => {
    const elapsedText =
      row.days_since_activity !== null && row.days_since_activity !== undefined
        ? `${row.days_since_activity}日`
        : "経過日数不明";

    if (row.follow_required) {
      return [
        "要フォロー判定です。",
        `理由：最終営業日から ${elapsedText} 経過しています。`,
      ]
        .filter(Boolean)
        .join("\n");
    }

    return [
      "通常案件です。",
      `最終営業日からの経過：${elapsedText}`,
    ].join("\n");
  };

  const hotCaseColumns: GridColDef<HotCaseRow>[] = [
    {
      field: "follow_required",
      headerName: "要確認",
      width: 110,
      sortable: true,
      renderCell: (params) => (
        <Tooltip
          arrow
          placement="top"
          title={
            <Box sx={{ whiteSpace: "pre-line", fontSize: 12, lineHeight: 1.65 }}>
              {getFollowRequiredReason(params.row)}
            </Box>
          }
        >
          <Chip
            size="small"
            icon={
              params.row.follow_required ? (
                <InfoOutlinedIcon sx={{ fontSize: "16px !important" }} />
              ) : undefined
            }
            label={params.row.follow_required ? "要フォロー" : "通常"}
            sx={{
              backgroundColor: params.row.follow_required
                ? "rgba(255,193,7,0.18)"
                : "rgba(159,176,192,0.12)",
              color: params.row.follow_required
                ? dashboardColors.warning
                : dashboardColors.textMuted,
              fontWeight: params.row.follow_required ? 900 : 700,
              border: params.row.follow_required
                ? "1px solid rgba(255,193,7,0.35)"
                : `1px solid ${dashboardColors.border}`,
              "& .MuiChip-icon": {
                color: "inherit",
              },
            }}
          />
        </Tooltip>
      ),
    },
    {
      field: "company_name",
      headerName: "会社名",
      flex: 1.1,
      minWidth: 190,
    },
    {
      field: "sales_status",
      headerName: "状況",
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.sales_status || "-"}
          sx={{
            fontWeight: 800,
            backgroundColor:
              params.row.sales_status === "商談進行中"
                ? "rgba(197,231,99,0.16)"
                : params.row.sales_status === "アポ獲得"
                ? "rgba(0,191,197,0.16)"
                : "rgba(159,176,192,0.12)",
            color:
              params.row.sales_status === "商談進行中"
                ? dashboardColors.accent
                : params.row.sales_status === "アポ獲得"
                ? "#00bfc5"
                : dashboardColors.textSoft,
            border:
              params.row.sales_status === "商談進行中"
                ? `1px solid ${dashboardColors.borderStrong}`
                : params.row.sales_status === "アポ獲得"
                ? "1px solid rgba(0,191,197,0.35)"
                : `1px solid ${dashboardColors.border}`,
          }}
        />
      ),
    },
    {
      field: "sales_person",
      headerName: "担当者",
      width: 120,
    },
    {
      field: "proposed_service",
      headerName: "提案サービス",
      width: 160,
    },
    {
      field: "next_action",
      headerName: "次回対応",
      width: 150,
    },
    {
      field: "sales_date",
      headerName: "最終営業日",
      width: 130,
      renderCell: (params) => (
        <Typography sx={{ color: dashboardColors.textSoft }}>
          {formatDate(params.row.sales_date)}
        </Typography>
      ),
    },
    {
      field: "days_since_activity",
      headerName: "経過",
      width: 95,
      renderCell: (params) => (
        <Typography
          sx={{
            fontWeight: params.row.follow_required ? 800 : 500,
            color: params.row.follow_required
              ? dashboardColors.warning
              : dashboardColors.textSoft,
          }}
        >
          {params.row.days_since_activity !== null
            ? `${params.row.days_since_activity}日`
            : "-"}
        </Typography>
      ),
    },
    {
      field: "latest_note",
      headerName: "ひとこと",
      flex: 1.4,
      minWidth: 240,
      renderCell: (params) => (
        <Typography
          sx={{
            color: dashboardColors.textSoft,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          }}
          title={params.row.latest_note}
        >
          {params.row.latest_note || "-"}
        </Typography>
      ),
    },
  ];

  const renderGoalBlock = (keyName: GoalKey) => {
    const isEditing = editingKey === keyName;
    const meta = goalMeta[keyName];
    const value = monthlyGoalData[keyName] || "";
    const isMain = keyName === "monthly_sales_goals";

    return (
      <Box
        sx={{
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 0.9,
          py: 0.75,
          borderRadius: 2,
          boxSizing: "border-box",
          overflow: "hidden",
          backgroundColor: isMain
            ? "rgba(197,231,99,0.11)"
            : "rgba(31,37,48,0.62)",
          border: isMain
            ? `1px solid ${dashboardColors.borderStrong}`
            : `1px solid ${dashboardColors.border}`,
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 1.6,
            display: { xs: "none", sm: "grid" },
            placeItems: "center",
            color: dashboardColors.accent,
            backgroundColor: "rgba(197,231,99,0.10)",
            border: `1px solid ${dashboardColors.borderStrong}`,
            flexShrink: 0,
          }}
        >
          {meta.icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.65}
            sx={{ minWidth: 0, mb: 0.25 }}
          >
            <Typography
              sx={{
                color: dashboardColors.textMuted,
                fontWeight: 800,
                fontSize: 10.8,
                lineHeight: 1.1,
                flexShrink: 0,
              }}
            >
              {meta.title}
            </Typography>

            <Box
              sx={{
                height: 1,
                flex: 1,
                minWidth: 8,
                backgroundColor: "rgba(255,255,255,0.08)",
              }}
            />
          </Stack>

          {isEditing ? (
            <Stack spacing={0.65} sx={{ minWidth: 0 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={3}
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: dashboardColors.text,
                    backgroundColor: "rgba(31,37,48,0.92)",
                    borderRadius: 1.6,
                    fontSize: 13,
                    fontWeight: 700,
                    p: 0.35,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: dashboardColors.border,
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: dashboardColors.accent,
                  },
                  "& textarea": {
                    color: dashboardColors.text,
                    fontWeight: 700,
                  },
                }}
              />

              <Stack
                direction="row"
                spacing={0.65}
                justifyContent="flex-end"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGoalEditCancel}
                  disabled={goalSaving}
                  sx={{
                    color: dashboardColors.textSoft,
                    borderColor: dashboardColors.border,
                    minWidth: 62,
                    height: 26,
                    fontSize: 11,
                    px: 1,
                  }}
                >
                  取消
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleGoalEditSave}
                  disabled={goalSaving}
                  startIcon={
                    goalSaving ? (
                      <CircularProgress size={13} color="inherit" />
                    ) : undefined
                  }
                  sx={{
                    backgroundColor: dashboardColors.accent,
                    color: "#111827",
                    fontWeight: 900,
                    minWidth: 52,
                    height: 26,
                    fontSize: 11,
                    px: 1,
                  }}
                >
                  {goalSaving ? "保存中" : "保存"}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Tooltip arrow title={value || "未入力"}>
              <Typography
                sx={{
                  color: value ? dashboardColors.text : dashboardColors.textMuted,
                  fontSize: { xs: 13.2, md: 13.8 },
                  fontWeight: value ? 850 : 700,
                  lineHeight: 1.35,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  cursor: value ? "help" : "default",
                  minHeight: 0,
                }}
              >
                {value || "未入力"}
              </Typography>
            </Tooltip>
          )}
        </Box>

        {!isEditing && (
          <IconButton
            size="small"
            onClick={() => handleGoalEditStart(keyName)}
            disabled={goalSaving}
            sx={{
              color: dashboardColors.textMuted,
              width: 24,
              height: 24,
              flexShrink: 0,
              "&:hover": {
                color: "#111827",
                backgroundColor: dashboardColors.accent,
              },
            }}
          >
            <ModeEditOutlineOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          ".MuiChartsTooltip-root": {
            color: `${dashboardColors.text} !important`,
          },

          ".MuiChartsTooltip-paper": {
            backgroundColor: `${dashboardColors.panelDark} !important`,
            color: `${dashboardColors.text} !important`,
            border: `1px solid ${dashboardColors.border}`,
            borderRadius: "8px",
            boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
          },

          ".MuiChartsTooltip-paper *": {
            color: `${dashboardColors.text} !important`,
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-table": {
            color: `${dashboardColors.text} !important`,
          },

          ".MuiChartsTooltip-table *": {
            color: `${dashboardColors.text} !important`,
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-table th": {
            color: `${dashboardColors.text} !important`,
            fontWeight: "900 !important",
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-table td": {
            color: `${dashboardColors.text} !important`,
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-labelCell": {
            color: `${dashboardColors.text} !important`,
            fontWeight: "700 !important",
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-valueCell": {
            color: `${dashboardColors.text} !important`,
            fontWeight: "900 !important",
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-markCell": {
            opacity: "1 !important",
          },

          ".MuiChartsTooltip-axisValueCell": {
            color: `${dashboardColors.text} !important`,
            fontWeight: "900 !important",
            opacity: "1 !important",
          },
        }}
      />
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          backgroundColor: dashboardColors.pageBg,
          minHeight: "100vh",
          overflowX: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minHeight: "100vh",
            boxSizing: "border-box",
            px: { xs: 1.25, sm: 1.5, md: 2 },
            py: { xs: 1.5, md: 2 },
            background:
              "radial-gradient(circle at top left, rgba(197,231,99,0.10), transparent 28%), #2D2C3A",
            overflowX: "hidden",
          }}
        >
          <Stack spacing={1.8}>
           
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage(null)}
                sx={{ borderRadius: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            {/* =====================================================
             月次目標
            ===================================================== */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 0.75, md: 0.85 },
                borderRadius: 2.6,
                background:
                  "linear-gradient(135deg, rgba(197,231,99,0.07), rgba(31,37,48,0.92) 46%, rgba(31,29,48,1))",
                border: `1px solid ${dashboardColors.border}`,
                boxShadow: "0 6px 16px rgba(0,0,0,0.14)",
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "108px minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 1fr)",
                  },
                  gap: 0.8,
                  alignItems: "stretch",
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    flexDirection: { xs: "row", md: "column" },
                    alignItems: { xs: "center", md: "flex-start" },
                    justifyContent: "center",
                    gap: { xs: 1, md: 0.55 },
                    px: 0.75,
                    py: 0.65,
                    borderRadius: 2,
                    backgroundColor: "rgba(31,37,48,0.44)",
                    border: `1px solid ${dashboardColors.border}`,
                    boxSizing: "border-box",
                  }}
                >
                  <Chip
                    size="small"
                    label={formatMonthLabel(selectedMonth)}
                    sx={{
                      height: 21,
                      backgroundColor: dashboardColors.accent,
                      color: "#111827",
                      fontWeight: 900,
                      fontSize: 10.5,
                      flexShrink: 0,
                      "& .MuiChip-label": {
                        px: 0.85,
                      },
                    }}
                  />

                  <Typography
                    sx={{
                      color: dashboardColors.text,
                      fontWeight: 950,
                      fontSize: { xs: 13.5, md: 15 },
                      letterSpacing: "0.03em",
                      whiteSpace: "nowrap",
                      lineHeight: 1.1,
                    }}
                  >
                    月方針
                  </Typography>
                </Box>

                <Box sx={{ minWidth: 0 }}>{renderGoalBlock("monthly_sales_goals")}</Box>
                <Box sx={{ minWidth: 0 }}>{renderGoalBlock("monthly_kpi")}</Box>
                <Box sx={{ minWidth: 0 }}>{renderGoalBlock("target")}</Box>
              </Box>
            </Paper>

            {/* =====================================================
             サマリー
            ===================================================== */}
            <Grid container spacing={2}>
              {summaryCards.map((item) => (
                <Grid key={item.label} size={{ xs: 12, sm: 6, md: 3 }}>
                  <DashboardCard
                    sx={{
                      border:
                        item.label === "要フォロー"
                          ? "1px solid rgba(255,193,7,0.45)"
                          : `1px solid ${dashboardColors.border}`,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
                      <Stack spacing={0.75}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{ color: dashboardColors.textSoft }}
                          >
                            {item.label}
                          </Typography>

                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              backgroundColor:
                                item.label === "要フォロー"
                                  ? "rgba(255,193,7,0.14)"
                                  : "rgba(197,231,99,0.12)",
                              color:
                                item.label === "要フォロー"
                                  ? dashboardColors.warning
                                  : dashboardColors.accent,
                            }}
                          >
                            {item.icon}
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "baseline" }}>
                          <Typography
                            sx={{
                              fontSize: { xs: 26, md: 32 },
                              fontWeight: 900,
                              color:
                                item.label === "要フォロー"
                                  ? dashboardColors.warning
                                  : dashboardColors.text,
                              lineHeight: 1,
                            }}
                          >
                            {item.value}
                          </Typography>

                          <Typography
                            sx={{
                              ml: 0.75,
                              color: dashboardColors.textMuted,
                              fontWeight: 700,
                            }}
                          >
                            {item.unit}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          sx={{
                            color: dashboardColors.textMuted,
                            lineHeight: 1.6,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </DashboardCard>
                </Grid>
              ))}
            </Grid>

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 260,
                  color: dashboardColors.textSoft,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
              </Box>
            ) : (
              <>
                {/* =====================================================
                 グラフエリア
                ===================================================== */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <DashboardCard>
                      <CardHeader
                        title="現在の案件進捗分布"
                        subheader={`会社ごとの現在ステータスを集計 / 管理対象：全${summaryData.total_customer_companies}社`}
                      />
                      <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
                        <BarChart
                          xAxis={[
                            {
                              scaleType: "band",
                              data: pipelineChartData.map((d) => d.label),
                            },
                          ]}
                          series={[
                            {
                              label: "会社数",
                              data: pipelineChartData.map((d) => d.value),
                            },
                          ]}
                          height={240}
                          sx={chartSx}
                        />
                      </CardContent>
                    </DashboardCard>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DashboardCard>
                      <CardHeader
                        title="月別 アポ獲得社数・成約社数"
                        subheader="同じ会社が同月に複数回あっても1社で集計"
                      />
                      <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
                        <BarChart
                          dataset={monthlyTrendChartData}
                          xAxis={[{ dataKey: "month", scaleType: "band" }]}
                          series={[
                            {
                              dataKey: "appointment_companies",
                              label: "アポ獲得社数",
                            },
                            {
                              dataKey: "won_companies",
                              label: "成約社数",
                            },
                          ]}
                          height={240}
                          sx={chartSx}
                        />
                      </CardContent>
                    </DashboardCard>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <DashboardCard>
                      <CardHeader
                        title="提案サービス別 関心社数 直近5年推移"
                        subheader="年別×サービス別で、関心の推移を確認できます"
                      />
                      <CardContent sx={{ p: { xs: 1.5, md: 1.8 }, "&:last-child": { pb: { xs: 1.5, md: 1.8 } } }}>
                        <BarChart
                          dataset={serviceInterestYearlyChartData}
                          xAxis={[
                            {
                              dataKey: "year",
                              scaleType: "band",
                            },
                          ]}
                          series={serviceInterestYearlySeries}
                          height={240}
                          sx={chartSx}
                        />

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: dashboardColors.textMuted,
                            lineHeight: 1.6,
                          }}
                        >
                          ※1社が複数サービスに該当する場合があります。
                        </Typography>
                      </CardContent>
                    </DashboardCard>
                  </Grid>
                </Grid>

                {/* =====================================================
                 HOT・要フォロー案件
                ===================================================== */}
                <DashboardCard>
                  <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                    <SectionTitle
                      title="アポ獲得・商談進行中・要フォロー案件"
                    />

                    <Box sx={{ height: 330, width: "100%" }}>
                      <DataGridPro
                        rows={hotCases}
                        columns={hotCaseColumns}
                        autoPageSize
                        disableRowSelectionOnClick
                        sx={gridSx}
                        initialState={{
                          sorting: {
                            sortModel: [
                              { field: "follow_required", sort: "desc" },
                              { field: "days_since_activity", sort: "desc" },
                            ],
                          },
                        }}
                      />
                    </Box>
                  </CardContent>
                </DashboardCard>

                {/* =====================================================
                 メンバー別対応状況
                ===================================================== */}
                <DashboardCard>
                  <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                    <SectionTitle
                      title="メンバー別 対応状況"
                    />

                    <Grid container spacing={1.2}>
                      {memberProgress.length === 0 ? (
                        <Grid size={{ xs: 12 }}>
                          <Paper
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: dashboardColors.panelDark,
                              border: `1px solid ${dashboardColors.border}`,
                              color: dashboardColors.textSoft,
                            }}
                          >
                            メンバー別の対応状況はありません。
                          </Paper>
                        </Grid>
                      ) : (
                        memberProgress.map((member) => {
                          const hasFollow = member.follow_required_companies > 0;
                          const totalAction =
                            member.current_month_appointment_companies +
                            member.current_month_won_companies;

                          return (
                            <Grid
                              key={member.sales_person_id || member.id}
                              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                            >
                              <Paper
                                sx={{
                                  height: "100%",
                                  p: 1.4,
                                  borderRadius: 2.5,
                                  backgroundColor: dashboardColors.panelDark,
                                  border: hasFollow
                                    ? "1px solid rgba(255,193,7,0.38)"
                                    : `1px solid ${dashboardColors.border}`,
                                  boxShadow: "0 8px 18px rgba(0,0,0,0.16)",
                                }}
                              >
                                <Stack spacing={1.1}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap={1}
                                  >
                                    <Typography
                                      sx={{
                                        color: dashboardColors.text,
                                        fontWeight: 900,
                                        fontSize: 14,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={member.sales_person}
                                    >
                                      {member.sales_person || "未設定"}
                                    </Typography>

                                    {hasFollow && (
                                      <Tooltip
                                        arrow
                                        title={`進行中案件のうち ${member.follow_required_companies}件 が30日以上動き無し。`}
                                      >
                                        <Chip
                                          size="small"
                                          label="要フォロー"
                                          sx={{
                                            height: 22,
                                            backgroundColor: "rgba(255,193,7,0.14)",
                                            color: dashboardColors.warning,
                                            border: "1px solid rgba(255,193,7,0.34)",
                                            fontWeight: 800,
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>

                                  <Grid container spacing={0.8}>
                                    <Grid size={{ xs: 6 }}>
                                      <Box>
                                        <Typography
                                          sx={{
                                            color: dashboardColors.textMuted,
                                            fontSize: 11,
                                            fontWeight: 700,
                                          }}
                                        >
                                          対応中
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color: dashboardColors.text,
                                            fontSize: 20,
                                            fontWeight: 900,
                                            lineHeight: 1.15,
                                          }}
                                        >
                                          {member.active_companies}
                                          <Typography
                                            component="span"
                                            sx={{
                                              ml: 0.3,
                                              color: dashboardColors.textMuted,
                                              fontSize: 11,
                                              fontWeight: 700,
                                            }}
                                          >
                                            件
                                          </Typography>
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid size={{ xs: 6 }}>
                                      <Box>
                                        <Typography
                                          sx={{
                                            color: dashboardColors.textMuted,
                                            fontSize: 11,
                                            fontWeight: 700,
                                          }}
                                        >
                                          今月成約
                                        </Typography>
                                        <Typography
                                          sx={{
                                            color:
                                              totalAction > 0
                                                ? dashboardColors.accent
                                                : dashboardColors.textSoft,
                                            fontSize: 20,
                                            fontWeight: 900,
                                            lineHeight: 1.15,
                                          }}
                                        >
                                          {totalAction}
                                          <Typography
                                            component="span"
                                            sx={{
                                              ml: 0.3,
                                              color: dashboardColors.textMuted,
                                              fontSize: 11,
                                              fontWeight: 700,
                                            }}
                                          >
                                            件
                                          </Typography>
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Grid>

                                  <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                                    <Chip
                                      size="small"
                                      label={`アポ ${member.current_month_appointment_companies}`}
                                      sx={{
                                        height: 22,
                                        backgroundColor: "rgba(197,231,99,0.10)",
                                        color: dashboardColors.textSoft,
                                        border: `1px solid ${dashboardColors.border}`,
                                        fontWeight: 700,
                                      }}
                                    />
                                    <Chip
                                      size="small"
                                      label={`成約 ${member.current_month_won_companies}`}
                                      sx={{
                                        height: 22,
                                        backgroundColor: "rgba(197,231,99,0.10)",
                                        color: dashboardColors.textSoft,
                                        border: `1px solid ${dashboardColors.border}`,
                                        fontWeight: 700,
                                      }}
                                    />
                                    <Chip
                                      size="small"
                                      label={`要 ${member.follow_required_companies}`}
                                      sx={{
                                        height: 22,
                                        backgroundColor: hasFollow
                                          ? "rgba(255,193,7,0.14)"
                                          : "rgba(159,176,192,0.10)",
                                        color: hasFollow
                                          ? dashboardColors.warning
                                          : dashboardColors.textMuted,
                                        border: hasFollow
                                          ? "1px solid rgba(255,193,7,0.34)"
                                          : `1px solid ${dashboardColors.border}`,
                                        fontWeight: 700,
                                      }}
                                    />
                                  </Stack>
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })
                      )}
                    </Grid>
                  </CardContent>
                </DashboardCard>
              </>
            )}
          </Stack>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={(_, reason) => {
              if (reason === "clickaway") return;

              setSnackbar((prev) => ({
                ...prev,
                open: false,
              }));
            }}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              variant="filled"
              onClose={() =>
                setSnackbar((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default Prospecting_strategy;